import { StaffLayout } from "@/components/layouts/staff-layout"
import { Badge } from "@/components/ui/badge"
import { MainContainer } from "@/components/ui/container"
import { DataTable } from "@/components/ui/data-table"
import type { AppliedSort, DataColumn } from "@/components/ui/data-table"
import { PageHeader } from "@/components/ui/headers"
import { t } from "@/lib/i18n"

type EventRow = {
  id: number
  locationId: number | null
  action: string
  aggregateType: string | null
  aggregateId: number | null
  aggregate: string | null
  actorType: string | null
  actorId: number | null
  actor: string | null
  eventName: string
  createdAt: string
}

type Pagination = {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
  previousPage: number | null
  nextPage: number | null
}

type Props = {
  events: EventRow[]
  pagination: Pagination
  sort: AppliedSort
  timeZone?: string
}

function actionLabel(action: string) {
  return action.replace(/_/g, " ")
}

const TABLE_PROPS = [ "events", "pagination", "sort" ]

const columns: DataColumn<EventRow>[] = [
  {
    accessorKey: "id",
    header: t("ui.react.pages.staff.location.manage.events.index.header_id"),
    field: { type: "title", mono: true },
  },
  {
    accessorKey: "createdAt",
    header: t("ui.react.pages.staff.location.manage.events.index.header_date"),
    field: { type: "datetime" },
    sortKey: "created_at",
    mobile: "meta",
  },
  {
    accessorKey: "eventName",
    header: t("ui.react.pages.staff.location.manage.events.index.header_event"),
    sortKey: "action",
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        <Badge variant="outline" className="w-fit capitalize">
          {actionLabel(row.original.action)}
        </Badge>
        <span className="text-muted-foreground text-xs">{row.original.eventName}</span>
      </div>
    ),
  },
  {
    accessorKey: "aggregate",
    header: t("ui.admin.events.headers.added"),
    field: { type: "code" },
    sortKey: "aggregate_type",
    mobile: "secondary",
  },
  {
    accessorKey: "actor",
    header: t("ui.admin.events.headers.actor"),
    cell: ({ row }) =>
      row.original.actor ? (
        <span className="text-xs">{row.original.actor}</span>
      ) : (
        <span className="text-muted-foreground text-xs">{t("ui.admin.events.actor.system")}</span>
      ),
  },
]

export default function StaffLocationManageEventsIndex({
  events,
  pagination,
  sort,
  timeZone,
}: Props) {
  return (
    <StaffLayout>
      <MainContainer size="md">
        <PageHeader
          title={t("ui.react.pages.staff.location.manage.events.index.events_location")}
          description={t("ui.react.pages.staff.location.manage.events.index.showing_events", {
            count: events.length,
            total: pagination.totalCount,
          })}
        />

        <DataTable
          mode="server"
          url="/manage/events"
          columns={columns}
          data={events}
          sort={sort}
          pagination={pagination}
          perPageOptions={[ 25, 50, 100 ]}
          reloadOnly={TABLE_PROPS}
          // The restaurant's own zone: a staff audit log read in the browser's
          // zone puts a shift's events on the wrong day. See react.md §16.
          timeZone={timeZone}
        />
      </MainContainer>
    </StaffLayout>
  )
}
